import { useRef, useState, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useGlobalContext } from "../Config";
import { useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { Panel } from 'primereact/panel';
import ClienteDetalleModal from './ClienteDetalleModal';


const FormCrearDocumento = ({
    isFormVisible,
    setIsFormVisible,
    empresa,
    setEmpresa,
    idContrato,
    setIdContrato,
    idEmpresa,
    setIdEmpresa,
    idCliente,
    setIdCliente,
    razonSocial,
    setRazonSocial,
    fetchDocumentos,
    toast,
}) => {
    const [loading, setLoading] = useState(false);
    const [tiposDocumento, setTiposDocumento] = useState([]);
    const [tiposDocumentoOptions, setTiposDocumentoOptions] = useState([]);
    const [plantillasDocumentoOptions, setPlantillasDocumentoOptions] = useState([]);
    const [plantillasDocumento, setPlantillasDocumento] = useState([]);
    const [tipoSeleccionado, setTipoSeleccionado] = useState(null);
    const [plantillaSeleccionada, setPlantillaSeleccionada] = useState(null);
    const [nombre, setNombre] = useState('');
    const [varsCollapsed, setVarsCollapsed] = useState(true);

    //estado simple para valores del formulario
    const [formValues, setFormValues] = useState({});

    //variables simples derivadas de variables_json (array u objeto)
    const variables = useMemo(() => {
        const raw = plantillaSeleccionada?.variables_json;
        if (!raw) return [];
        try {
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
            if (Array.isArray(parsed)) {
                // ["rut", "cliente_nombre"] o [{name:"rut"}]
                return parsed
                    .map(v => (typeof v === 'string' ? v : v?.name || v?.key || v?.campo || ''))
                    .filter(Boolean);
            }
            if (parsed && typeof parsed === 'object') {
                // { rut: {...}, cliente_nombre: {...} }
                return Object.keys(parsed);
            }
            return [];
        } catch {
            return [];
        }
    }, [plantillaSeleccionada]);

    //inicializar/ajustar los valores cuando cambian las variables
    useEffect(() => {
        setFormValues(prev => {
            const next = {};
            variables.forEach(k => {
                next[k] = prev[k] ?? null;
            });
            return next;
        });
    }, [variables]);

    // render básico por tipo inferido
    const renderInput = (key) => {
        const id = `var-${key}`;
        const val = formValues[key];
        const setVal = (newVal) => setFormValues({ ...formValues, [key]: newVal });

        const lower = String(key).toLowerCase();
        if (lower.includes('fecha') || lower.includes('desde') || lower.includes('hasta')) {
            return (
                <span className="p-float-label mt-2 mb-3">
                    <Calendar
                        id={id}
                        value={val || null}
                        onChange={(e) => setVal(e.value)}
                        className="p-inputtext-lg"
                        dateFormat="dd/mm/yy"
                    />
                    <label htmlFor={id}>{key.replace(/_/g, ' ')}</label>
                </span>
            );
        }
        if (/(precio|monto|valor|tarifa)/i.test(lower)) {
            return (
                <span className="p-float-label mt-2 mb-3">
                    <InputNumber
                        id={id}
                        value={val ?? null}
                        onValueChange={(e) => setVal(e.value)}
                        className="p-inputtext-lg"
                        mode="decimal"
                        minFractionDigits={0}
                        maxFractionDigits={2}
                    />
                    <label htmlFor={id}>{key.replace(/_/g, ' ')}</label>
                </span>
            );
        }
        return (
            <span className="p-float-label mt-2 mb-3">
                <InputText
                    id={id}
                    value={val ?? ''}
                    onChange={(e) => setVal(e.target.value)}
                    className="p-inputtext-lg"
                />
                <label htmlFor={id}>{key.replace(/_/g, ' ')}</label>
            </span>
        );
    };

    const { baseUrl, urlContratos } = useGlobalContext();

    useEffect(() => {
        if (isFormVisible && empresa) {
            obtenerTiposDocumentoByEmpresa();
            
        }
        if (tipoSeleccionado) {
            obtenerPlantillasDocumentoByEmpresa(tipoSeleccionado.id);
        }
    }, [isFormVisible, urlContratos, empresa, tipoSeleccionado]);


    const obtenerTiposDocumentoByEmpresa = async () => {
        try {
            const response = await fetch(
                `${urlContratos}getTiposDocumentoByEmpresa?empresa=${empresa}`,
                {
                    method: 'GET',
                    credentials: 'include',
                }
            );
            const data = await response.json();
            const lista = Array.isArray(data.tiposDocumentos) ? data.tiposDocumentos : Object.values(data.tiposDocumentos || {});
            setTiposDocumentoOptions(lista);
        } catch (error) {
            console.error("Error al obtener los tipos de documento:", error);
        }
    };

    const obtenerPlantillasDocumentoByEmpresa = async (idTipoDocumento) => {
        try {
            const response = await fetch(
                `${urlContratos}getPlantillasDocumentoByEmpresa?empresa=${empresa}&idTipo=${idTipoDocumento}`,
                {
                    method: 'GET',
                    credentials: 'include',
                }
            );
            const data = await response.json();
            const lista = Array.isArray(data.plantillasDocumentos) ? data.plantillasDocumentos : Object.values(data.plantillasDocumentos || {});
            setPlantillasDocumentoOptions(lista);
        } catch (error) {
            console.error("Error al obtener las plantillas de documento:", error);
        }
    };

    // Botón Guardar: envía la plantilla al backend
    const handleGuardar = async () => {
        try {
            setLoading(true);  
            if (!empresa || !tipoSeleccionado || !plantillaSeleccionada || !nombre) {
                toast.current?.show({ severity: 'warn', summary: 'Campos requeridos', detail: 'Complete todos los campos requeridos', life: 3000 });
                return;
            }

            // Normaliza metadatos: convierte Date a 'YYYY-MM-DD'
            const normalizeMetadata = (values) =>
                Object.fromEntries(
                    Object.entries(values).map(([k, v]) => {
                        if (v instanceof Date) {
                            const y = v.getFullYear();
                            const m = String(v.getMonth() + 1).padStart(2, '0');
                            const d = String(v.getDate()).padStart(2, '0');
                            return [k, `${y}-${m}-${d}`];
                        }
                        return [k, v];
                    })
                );

            const payload = {
                idEmpresa: idEmpresa,
                idPlantilla: plantillaSeleccionada?.id ?? null,
                idContrato: idContrato ?? null,
                idUsuario: Number(sessionStorage.getItem('usuario_id') || 0),
                idTipoDocumento: tipoSeleccionado?.id ?? null,
                idEstadoDocumento: 1,
                filename: nombre || 'documento.docx',
                metadataJson: normalizeMetadata(formValues),
            };

            const response = await fetch(`${urlContratos}guardarMetadataDocumento`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                credentials: 'include',
            });

            if (!response.ok) {
                let detail = `El documento ingreso ya existe, porfavor reingrese tipo o plantilla diferente.`;
                try {
                    const errData = await response.json();
                    detail = errData?.message || detail;
                } catch {
                    try {
                        const errText = await response.text();
                        detail = errText || detail;
                    } catch {}
                }

                toast.current?.show({
                    severity: 'error',
                    summary: 'Error al guardar',
                    detail,
                    life: 4000
                });
                return;
            }else{
                await fetchDocumentos();
                toast.current?.show({
                    severity: 'success',
                    summary: 'Éxito',
                    detail: 'Documento creado con éxito',
                    life: 3000
                });
            }
        } catch (err) {
            console.error(err);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el documento', life: 4000 });
        } finally {
            setLoading(false);  
            resetFormDocumento();
            setIsFormVisible(false);
        }
    };

    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [showClienteModal, setShowClienteModal] = useState(false);
    const openClienteModal = async (idCliente) => {
        const response = await fetch(`${baseUrl}getClienteById?idCliente=${idCliente}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });
        const data = await response.json();
        setClienteSeleccionado(data);
        setShowClienteModal(true);
    };
    const closeClienteModal = () => {
        setShowClienteModal(false);
        setClienteSeleccionado(null);
    };

    const resetFormDocumento = () => {
        setTipoSeleccionado(null);
        setPlantillaSeleccionada(null);
        setNombre('');
        setEmpresa(null);
        setRazonSocial('');
        setIdCliente(null);
        setIdEmpresa(null);
        setIdContrato(null);
        setFormValues({});
    };

    const handleDialogHide = () => {
        resetFormDocumento();
        setIsFormVisible(false);
    };

    return (
        <div>
            <Dialog
                visible={isFormVisible}
                onHide={handleDialogHide}
                className="p-fluid product-dialog"
                closable={handleDialogHide}
                modal
                footer={
                    <div className="flex justify-content-end mt-3">
                        <Button
                            label="Guardar"
                            icon={loading ? "pi pi-spin pi-spinner" : "pi pi-save"}
                            className="p-button-success p-button-rounded"
                            onClick={handleGuardar}
                            disabled={!empresa || !tiposDocumento || !plantillasDocumento || !nombre || loading}
                            raised
                        />
                    </div>
                }
                header="Crear Documento"
            >
                <div className="card">
                    <h6 className="mb-3">
                        Información del Documento
                    </h6>                

                    <h7>
                        Cliente:<Button
                                    className="p-button-link p-0"
                                    label={razonSocial || "Sin razón social"}
                                    onClick={() => openClienteModal(idCliente)}
                                    tooltip="Ver detalles del cliente"
                                    tooltipOptions={{ position: "top" }}
                                    draggable={false}
                                /><br/>
                        Empresa Arrendadora: {empresa}<br/>

                        N° de contrato: {idContrato}
                    </h7>   


                    <div className="grid mt-2 mb-3">
                        <div className="col-12 md:col-4">
                            <span className="p-float-label w-full">
                                <Dropdown
                                    id="tipoDocumento"
                                    value={tipoSeleccionado}
                                    onChange={(e) => setTipoSeleccionado(e.value)}
                                    options={tiposDocumentoOptions}
                                    optionLabel="descripcion"
                                    placeholder="Seleccione un tipo de documento"
                                    className="w-full p-inputtext-lg"
                                    required
                                    disabled = {loading}
                                />
                                <label htmlFor="tipoDocumento">Tipo de Documento</label>
                            </span>
                        </div>

                        <div className="col-12 md:col-4">
                            <span className="p-float-label w-full">
                                <Dropdown
                                    id="plantillaDocumento"
                                    value={plantillaSeleccionada}
                                    onChange={(e) => setPlantillaSeleccionada(e.value)}
                                    options={plantillasDocumentoOptions}
                                    optionLabel="nombre"
                                    placeholder="Seleccione Plantilla"
                                    className="w-full p-inputtext-lg"
                                    required
                                    disabled = {!tipoSeleccionado || loading}
                                />
                                <label htmlFor="plantillaDocumento">Plantilla de Documento</label>
                            </span>
                        </div>

                        <div className="col-12 md:col-4">
                            <span className="p-float-label w-full">
                                <InputText
                                    id="nombre"
                                    value={nombre}
                                    onChange={(e) => setNombre(e.target.value)}
                                    placeholder="Ingrese nombre del archivo"
                                    maxLength={100}
                                    className="w-full p-inputtext-lg"
                                    required
                                    disabled = {loading}
                                />
                                <label htmlFor="nombre">Nombre de Documento</label>
                            </span>
                        </div>
                    </div>

                    {/* renderiza un input por cada variable, inicialmente colapsado */}
                    <Panel
                        header="Variables del Documento"
                        toggleable
                        collapsed={varsCollapsed}
                        onToggle={(e) => setVarsCollapsed(e?.value ?? !varsCollapsed)}
                        className="mt-3"
                        disabled = {loading}
                    >
                        <div className="grid">
                            {variables.length === 0 ? (
                                <div className="col-12 text-500">Seleccione una plantilla con variables.</div>
                            ) : (
                                variables.map((key) => (
                                    <div key={key} className="col-12 md:col-6">
                                        {renderInput(key)}
                                    </div>
                                ))
                            )}
                        </div>
                    </Panel>

                </div>
            </Dialog>

            <ClienteDetalleModal
                visible={showClienteModal}
                onHide={closeClienteModal}
                cliente={clienteSeleccionado}
            />
        </div>
    );
}
export default FormCrearDocumento;
